import { Component, input, Input, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { toSignal } from '@angular/core/rxjs-interop';
import { COMMITMENTS_INTERACTION_BAR_TYPE } from '../../../constants/interaction-bar.constants';
import { commitmentsActions } from '../../../../store/commitments/commitments.actions';
import { NotificationsService } from '../../../../services/notifications.service';
import {
  IChangeOrderType,
  ISidebarCost,
  ISidebarInvoiceCost,
} from '../../../../store/commitments/commitments.types';
import { dropdownAlignRight } from '../../../overlays/option-list.constants';
import { OverlayGeneralComponent } from '../../../overlays/overlay-general/overlay-general.component';
import { CostViewTableDropdownComponent } from '../../cost-view-table-dropdown/cost-view-table-dropdown.component';
import { AddRemoveButtonsComponent } from '../../../buttons/add-remove-buttons/add-remove-buttons.component';
import { FloatingInputComponent } from '../../../inputs/floating-input/floating-input.component';
import { commitmentsSelectors } from '../../../../store/commitments/commitments.selectors';
import { CHANGE_ORDER_TYPE } from '../../../../store/commitments/commitments.constants';
import { DropdownComponent } from '@app/framework/inputs/dropdown/dropdown.component';

@Component({
  selector: 'app-cost-description',
  templateUrl: './cost-description.component.html',
  styleUrls: ['./cost-description.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    CdkOverlayOrigin,
    OverlayGeneralComponent,
    CostViewTableDropdownComponent,
    AddRemoveButtonsComponent,
    FloatingInputComponent,
    DropdownComponent,
  ],
})
export class CostDescriptionComponent {
  protected readonly COMMITMENTS_INTERACTION_BAR_TYPE = COMMITMENTS_INTERACTION_BAR_TYPE;
  protected readonly dropdownAlignRight = dropdownAlignRight;

  _model: Partial<ISidebarCost | ISidebarInvoiceCost>;
  @ViewChild('costDescriptionForm') costDescriptionForm: NgForm;
  @Input() commitmentType: COMMITMENTS_INTERACTION_BAR_TYPE;
  @Input() set model(value: Partial<ISidebarCost | ISidebarInvoiceCost>) {
    if (value) {
      this._model = value;
      if (this.commitmentType === COMMITMENTS_INTERACTION_BAR_TYPE.CHANGE_ORDER) {
        this._model.is_dropdown = this.changeOrderType !== CHANGE_ORDER_TYPE.SCOPE;
      }
    }
  }

  get model(): Partial<ISidebarCost | ISidebarInvoiceCost> {
    return this._model;
  }
  @Input() costIndex: number;
  @Input() spendEntryIndex: number;

  isDropdownShown = {
    name: [],
  };
  @Input() totalCostCount: number;

  private _changeOrderType: IChangeOrderType = null;
  @Input() set changeOrderType(value: IChangeOrderType) {
    if (!value) {
      return;
    }
    this._changeOrderType = value;
    this.model.is_dropdown = value !== CHANGE_ORDER_TYPE.SCOPE;
  }
  get changeOrderType(): IChangeOrderType {
    return this._changeOrderType;
  }

  primeContractSOVs = toSignal(this.store.select(commitmentsSelectors.selectSideBarContractSOVs));

  disabledOptionIds = input<Set<string | number>>();
  disableAddDescription = input(false);
  disabledAddDescriptionTooltip = input<string>(null);
  disableRemoveDescriptionTooltip = input<string>(null);

  constructor(
    private store: Store,
    private notif: NotificationsService,
  ) {}

  async askRemoveSubItem() {
    const answer = await this.notif.showPopup('Do you want to remove this description?');
    if (answer) {
      this.deleteCost();
    }
  }

  updateModel() {
    let action;
    switch (this.commitmentType) {
      case COMMITMENTS_INTERACTION_BAR_TYPE.PC_TO_CONTRACT: {
        const sovId = this.model.sov_id ?? this.model.name;
        const selectedSOV = this.primeContractSOVs().find((sov) => sov.sov_id === sovId);
        this.model.value = selectedSOV.value;
        this.model.sov_id = selectedSOV.sov_id;
        action = commitmentsActions.updateContractSpendEntryCost;
        break;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.ADD_CONTRACT: {
        action = commitmentsActions.updateContractSpendEntryCost;
        break;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.CHANGE_ORDER: {
        action = commitmentsActions.updateChangeOrderSpendEntryCost;
        break;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.INVOICES: {
        action = commitmentsActions.updateInvoiceCostValue;
        break;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.DIRECT_COST: {
        action = commitmentsActions.updateDirectCostSpendEntryDescription;
        break;
      }
    }

    if (!action) {
      console.error('Unhandled store action in cost description update');
      return;
    }

    console.log(this.model);
    if (this.commitmentType === COMMITMENTS_INTERACTION_BAR_TYPE.INVOICES) {
      this.store.dispatch(
        action({
          costIndex: this.costIndex,
          cost: this.model,
        }),
      );
    } else {
      this.store.dispatch(
        action({
          costIndex: this.costIndex,
          spendEntryIndex: this.spendEntryIndex,
          cost: this.model,
        }),
      );
    }
  }

  addCost() {
    // if (!this.model?.item?.id) {
    //   return;
    // }
    switch (this.commitmentType) {
      case COMMITMENTS_INTERACTION_BAR_TYPE.PC_TO_CONTRACT:
      case COMMITMENTS_INTERACTION_BAR_TYPE.ADD_CONTRACT:
        this.store.dispatch(
          commitmentsActions.addCostToContractCommitmentItem({
            spendEntryIndex: this.spendEntryIndex,
            newCostIndex: this.costIndex + 1,
          }),
        );
        break;
      case COMMITMENTS_INTERACTION_BAR_TYPE.CHANGE_ORDER:
        this.store.dispatch(
          commitmentsActions.addCostToChangeOrderCommitmentItem({
            spendEntryIndex: this.spendEntryIndex,
            newCostIndex: this.costIndex + 1,
          }),
        );
        break;
      case COMMITMENTS_INTERACTION_BAR_TYPE.DIRECT_COST:
        this.store.dispatch(
          commitmentsActions.addCostToDirectCostCommitmentItem({
            spendEntryIndex: this.spendEntryIndex,
            newCostIndex: this.costIndex + 1,
          }),
        );
        break;
      case COMMITMENTS_INTERACTION_BAR_TYPE.INVOICES:
        this.store.dispatch(commitmentsActions.addCostToInvoice());
        break;
      default:
        console.warn('Action not handled.');
        break;
    }
  }

  deleteCost() {
    let action;
    switch (this.commitmentType) {
      case COMMITMENTS_INTERACTION_BAR_TYPE.PC_TO_CONTRACT:
      case COMMITMENTS_INTERACTION_BAR_TYPE.ADD_CONTRACT: {
        action = commitmentsActions.removeContractSpendEntryCost;
        break;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.CHANGE_ORDER: {
        action = commitmentsActions.removeChangeOrderSpendEntryCost;
        break;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.INVOICES: {
        action = commitmentsActions.removeInvoiceCost;
        break;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.DIRECT_COST: {
        action = commitmentsActions.removeDirectCostEntryDescription;
        break;
      }
    }

    if (!action) {
      console.error('Unhandled store action when deleting cost description');
      return;
    }

    this.store.dispatch(
      action({
        spendEntryIndex: this.spendEntryIndex,
        costIndex: this.costIndex,
      }),
    );
  }
}
